import React from "react";
import axios from "axios";
import { formatInTimeZone } from "date-fns-tz";
import {
  MdCheckCircle,
  MdChevronRight,
  MdDevices,
  MdHistory,
  MdNotificationsActive,
  MdOutlineRefresh,
  MdPhoneIphone,
  MdSchedule,
  MdWarningAmber,
} from "react-icons/md";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import "./lesson-confirmation-pilot-monitoring.css";

const ENROLLMENT_OPTIONS = [
  { value: "all", label: "All enrollment statuses" },
  { value: "enabled", label: "Enabled" },
  { value: "invited", label: "Invited" },
  { value: "paused", label: "Paused" },
  { value: "disabled", label: "Disabled" },
];

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (value, timeZone, pattern = "MMM d, yyyy · h:mm a zzz") => {
  if (!value) return "—";
  try {
    return formatInTimeZone(new Date(value), timeZone || "America/New_York", pattern);
  } catch (_error) {
    return "—";
  }
};

const normalizeError = (error, fallback) => ({
  status: error?.response?.status || null,
  message: error?.response?.data?.error || error?.message || fallback,
});

const StatusChip = ({ value, danger = false, good = false }) => (
  <span
    className={`pilot-monitoring__chip${danger ? " danger" : ""}${
      good ? " good" : ""
    }`}
  >
    {titleCase(value) || "Unknown"}
  </span>
);

const ErrorNotice = ({ error, onRetry }) => {
  if (!error) return null;
  return (
    <div className="pilot-monitoring__error" role="alert">
      <MdWarningAmber aria-hidden="true" />
      <div>
        <strong>
          {error.status === 403
            ? "Administrator access required"
            : "Unable to load pilot monitoring"}
        </strong>
        <p>{error.message}</p>
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
};

const MonitoringSkeleton = () => (
  <div className="pilot-monitoring__skeleton" aria-label="Loading pilot monitoring">
    <section>{[1, 2, 3, 4, 5].map((item) => <span key={item} />)}</section>
    {[1, 2, 3].map((row) => (
      <div key={row}>{[1, 2, 3, 4, 5].map((cell) => <span key={cell} />)}</div>
    ))}
  </div>
);

const ReminderDetails = ({ reminder, timeZone, fallbackStatus, fallbackError }) => {
  if (!reminder) {
    return (
      <div className="pilot-monitoring__reminder">
        <StatusChip
          value={fallbackStatus || "not stored"}
          danger={fallbackStatus === "failed"}
        />
        {fallbackError ? (
          <p className="pilot-monitoring__delivery-error">{fallbackError}</p>
        ) : (
          <span className="pilot-monitoring__muted">No reminder stored</span>
        )}
      </div>
    );
  }
  const failed =
    ["failed", "no_devices"].includes(reminder.status) ||
    reminder.delivery?.status === "failed";
  return (
    <div className="pilot-monitoring__reminder">
      <div>
        <StatusChip value={reminder.delivery?.status || reminder.status} danger={failed} />
        <small>{reminder.delivery?.attempts || 0} delivery attempt{reminder.delivery?.attempts === 1 ? "" : "s"}</small>
      </div>
      <dl>
        <div><dt>Stored</dt><dd>{formatDate(reminder.storedAt, timeZone)}</dd></div>
        <div><dt>Sent</dt><dd>{formatDate(reminder.sentAt, timeZone)}</dd></div>
        <div><dt>Opened</dt><dd>{formatDate(reminder.openedAt, timeZone)}</dd></div>
        <div><dt>Actioned</dt><dd>{formatDate(reminder.actionedAt, timeZone)}</dd></div>
      </dl>
      {reminder.delivery?.error && (
        <p className="pilot-monitoring__delivery-error">{reminder.delivery.error}</p>
      )}
    </div>
  );
};

const HistoryModal = ({ teacher, onClose }) => {
  const [result, setResult] = React.useState({ items: [], nextCursor: null });
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);

  const loadHistory = React.useCallback(
    async ({ cursor = "", append = false } = {}) => {
      append ? setLoadingMore(true) : setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${baseUrl}/native/lesson-confirmations/admin/pilot-monitoring/${encodeURIComponent(
            teacher.id
          )}/history`,
          {
            ...getRequestHeaders(),
            params: { limit: 25, ...(cursor ? { cursor } : {}) },
          }
        );
        setResult((current) => ({
          ...response.data,
          items: append
            ? [...current.items, ...(response.data?.items || [])]
            : response.data?.items || [],
        }));
      } catch (requestError) {
        setError(normalizeError(requestError, "Unable to load this teacher's history."));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [teacher.id]
  );

  React.useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return (
    <div className="pilot-monitoring__modal-backdrop" onMouseDown={onClose}>
      <section
        className="pilot-monitoring__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pilot-history-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p>Native app history</p>
            <h2 id="pilot-history-title">{teacher.name}</h2>
            <span>{teacher.school || "No school recorded"}</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close teacher history">×</button>
        </header>
        <div className="pilot-monitoring__modal-body">
          <ErrorNotice error={error} onRetry={() => loadHistory()} />
          {loading ? (
            <MonitoringSkeleton />
          ) : !error && result.items.length === 0 ? (
            <div className="pilot-monitoring__history-empty">
              <MdHistory aria-hidden="true" />
              <strong>No real confirmation-day history yet</strong>
              <span>Demo confirmation days are intentionally excluded.</span>
            </div>
          ) : !error ? (
            <div className="pilot-monitoring__history-list">
              {result.items.map((item) => (
                <article key={item.id} className={item.missed ? "missed" : ""}>
                  <header>
                    <div>
                      <strong>{formatDate(`${item.serviceDate}T12:00:00.000Z`, item.timeZone, "EEEE, MMM d, yyyy")}</strong>
                      <span>{item.lessonCount} lesson{item.lessonCount === 1 ? "" : "s"} · {item.answeredCount} answered</span>
                    </div>
                    <StatusChip
                      value={item.missed ? "missed" : item.pending ? "pending" : item.status}
                      danger={item.missed}
                      good={["submitted", "locked"].includes(item.status)}
                    />
                  </header>
                  <div className="pilot-monitoring__history-grid">
                    <section>
                      <span>Confirmation deadline</span>
                      <strong>{formatDate(item.reminderDueAt, item.timeZone)}</strong>
                      <small>{item.pendingCount} lesson{item.pendingCount === 1 ? "" : "s"} still pending</small>
                    </section>
                    <section>
                      <span>Submitted</span>
                      <strong>{formatDate(item.submittedAt, item.timeZone)}</strong>
                      <small>Last opened {formatDate(item.lastOpenedAt, item.timeZone)}</small>
                    </section>
                    <ReminderDetails reminder={item.reminder} timeZone={item.timeZone} fallbackStatus={item.pushState} fallbackError={item.pushLastError} />
                  </div>
                </article>
              ))}
              {result.nextCursor && (
                <button
                  type="button"
                  className="pilot-monitoring__load-more"
                  onClick={() => loadHistory({ cursor: result.nextCursor, append: true })}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading…" : "Load older history"}
                </button>
              )}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};

const LessonConfirmationPilotMonitoring = ({ userData }) => {
  const [status, setStatus] = React.useState("all");
  const [cursor, setCursor] = React.useState("");
  const [cursorHistory, setCursorHistory] = React.useState([]);
  const [result, setResult] = React.useState({
    items: [],
    summary: {},
    nextCursor: null,
    missedConfirmationPolicy: null,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [historyTeacher, setHistoryTeacher] = React.useState(null);

  const loadOverview = React.useCallback(
    async (requestedCursor = cursor) => {
      if (!userData?.IsAdmin) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${baseUrl}/native/lesson-confirmations/admin/pilot-monitoring`,
          {
            ...getRequestHeaders(),
            params: {
              status,
              limit: 25,
              ...(requestedCursor ? { cursor: requestedCursor } : {}),
            },
          }
        );
        setResult({
          items: response.data?.items || [],
          summary: response.data?.summary || {},
          nextCursor: response.data?.nextCursor || null,
          missedConfirmationPolicy:
            response.data?.missedConfirmationPolicy || null,
          activeInstallationWindowDays:
            response.data?.activeInstallationWindowDays || null,
        });
      } catch (requestError) {
        setError(normalizeError(requestError, "Unable to load pilot monitoring."));
      } finally {
        setLoading(false);
      }
    },
    [cursor, status, userData?.IsAdmin]
  );

  React.useEffect(() => {
    if (userData?.IsAdmin) loadOverview(cursor);
  }, [cursor, loadOverview, userData?.IsAdmin]);

  if (!userData?.IsAdmin) return null;

  const changeStatus = (value) => {
    setStatus(value);
    setCursor("");
    setCursorHistory([]);
  };

  const previousPage = () => {
    setCursorHistory((history) => {
      if (!history.length) return history;
      setCursor(history[history.length - 1] || "");
      return history.slice(0, -1);
    });
  };

  const nextPage = () => {
    if (!result.nextCursor) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(result.nextCursor);
  };

  const summary = result.summary || {};
  return (
    <>
      <header className="pilot-monitoring__header">
        <div>
          <p>Phase 4</p>
          <h1>Native Pilot Monitoring</h1>
          <span>Monitor app readiness and missed lesson confirmations without sending notifications.</span>
        </div>
        <button type="button" onClick={() => loadOverview(cursor)} disabled={loading}>
          <MdOutlineRefresh aria-hidden="true" />
          {loading ? "Refreshing…" : "Refresh status"}
        </button>
      </header>

      <div className="pilot-monitoring__policy">
        <MdSchedule aria-hidden="true" />
        <div>
          <strong>Server-defined missed confirmation</strong>
          <span>{result.missedConfirmationPolicy?.description || "Waiting for the backend missed-confirmation policy."}</span>
          <small>Demo days are excluded. This page is read-only and never sends follow-up pushes.</small>
        </div>
      </div>

      <section className="pilot-monitoring__summary" aria-label="Pilot monitoring summary">
        <div><MdPhoneIphone aria-hidden="true" /><span>Enrolled teachers</span><strong>{summary.enrolledTeachers || 0}</strong></div>
        <div><MdCheckCircle aria-hidden="true" /><span>Enabled</span><strong>{summary.enabledTeachers || 0}</strong></div>
        <div><MdDevices aria-hidden="true" /><span>Active push devices</span><strong>{summary.activePushDevices || 0}</strong></div>
        <div className={(summary.pendingConfirmationDays || 0) > 0 ? "warning" : ""}><MdSchedule aria-hidden="true" /><span>Pending days</span><strong>{summary.pendingConfirmationDays || 0}</strong></div>
        <div className={(summary.missedConfirmationDays || 0) > 0 ? "danger" : ""}><MdWarningAmber aria-hidden="true" /><span>Missed days</span><strong>{summary.missedConfirmationDays || 0}</strong></div>
      </section>

      <section className="pilot-monitoring__filters" aria-label="Pilot monitoring filters">
        <label>
          Enrollment status
          <select aria-label="Pilot enrollment status" value={status} onChange={(event) => changeStatus(event.target.value)}>
            {ENROLLMENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <div>
          <MdNotificationsActive aria-hidden="true" />
          <span>A recent heartbeat means within {result.activeInstallationWindowDays || 45} days.</span>
        </div>
      </section>

      <ErrorNotice error={error} onRetry={() => loadOverview(cursor)} />
      {loading ? (
        <MonitoringSkeleton />
      ) : !error && result.items.length === 0 ? (
        <div className="pilot-monitoring__empty">
          <MdPhoneIphone aria-hidden="true" />
          <h2>No enrolled teachers match this filter</h2>
          <p>Enrollment records will appear here as the native-app pilot expands.</p>
        </div>
      ) : !error ? (
        <div className="pilot-monitoring__table-card">
          <div className="pilot-monitoring__table-scroll">
            <table className="pilot-monitoring__table">
              <thead>
                <tr><th>Teacher</th><th>Enrollment</th><th>App &amp; heartbeat</th><th>Push readiness</th><th>Latest confirmation</th><th>Confirmation queue</th><th>Last submission</th><th><span className="sr-only">History</span></th></tr>
              </thead>
              <tbody>
                {result.items.map((item) => {
                  const timeZone = item.lastScheduledConfirmation?.timeZone;
                  const failed = item.attentionReasons?.includes("delivery_failure");
                  return (
                    <tr key={item.teacher.id} className={item.missedConfirmationDays > 0 ? "missed" : ""}>
                      <td><strong>{item.teacher.name}</strong><small>{item.teacher.school || "No school"}</small><small>{item.teacher.id}</small></td>
                      <td><StatusChip value={item.enrollment.status} good={item.enrollment.status === "enabled"} /><div className="pilot-monitoring__features">{(item.enrollment.enabledFeatures || []).map((feature) => <span key={feature}>{titleCase(feature)}</span>)}</div></td>
                      <td>{item.installation ? <><strong>{titleCase(item.installation.platform)} · v{item.installation.appVersion || "unknown"}</strong><small>Build {item.installation.buildVersion || "—"}</small><small className={item.installation.recent ? "good-text" : "danger-text"}>{item.installation.recent ? "Recent" : "Stale"} · {formatDate(item.installation.lastSeenAt, timeZone)}</small></> : <span className="pilot-monitoring__muted">No installation heartbeat</span>}</td>
                      <td><strong className={item.installation?.notificationPermission === "granted" ? "good-text" : "danger-text"}>{titleCase(item.installation?.notificationPermission || "unknown permission")}</strong><small>{item.activePushDeviceCount} active push device{item.activePushDeviceCount === 1 ? "" : "s"}</small></td>
                      <td>{item.lastScheduledConfirmation ? <><strong>{item.lastScheduledConfirmation.serviceDate}</strong><small>Deadline {formatDate(item.lastScheduledConfirmation.reminderDueAt, timeZone)}</small><small>Stored {formatDate(item.reminder?.storedAt, timeZone)}</small><small>Sent {formatDate(item.reminder?.sentAt, timeZone)}</small><small>Opened {formatDate(item.reminder?.openedAt, timeZone)}</small><small>Actioned {formatDate(item.reminder?.actionedAt, timeZone)}</small><StatusChip value={item.reminder?.delivery?.status || item.lastScheduledConfirmation.pushState} danger={failed} /></> : <span className="pilot-monitoring__muted">No scheduled confirmation</span>}</td>
                      <td><div className="pilot-monitoring__queue-counts"><span><strong>{item.pendingConfirmationDays}</strong> pending</span><span className={item.missedConfirmationDays > 0 ? "danger-text" : ""}><strong>{item.missedConfirmationDays}</strong> missed</span></div>{failed && <small className="danger-text">Latest delivery failed</small>}</td>
                      <td><strong>{formatDate(item.lastSuccessfulSubmissionAt, timeZone)}</strong></td>
                      <td><button type="button" className="pilot-monitoring__history-button" onClick={() => setHistoryTeacher(item.teacher)} aria-label={`View pilot history for ${item.teacher.name}`}>History <MdChevronRight aria-hidden="true" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <footer className="pilot-monitoring__pagination">
            <span>{result.items.length} teacher{result.items.length === 1 ? "" : "s"} on this page</span>
            <div><button type="button" onClick={previousPage} disabled={!cursorHistory.length}>Previous</button><button type="button" onClick={nextPage} disabled={!result.nextCursor}>Next</button></div>
          </footer>
        </div>
      ) : null}

      {historyTeacher && <HistoryModal teacher={historyTeacher} onClose={() => setHistoryTeacher(null)} />}
    </>
  );
};

export default LessonConfirmationPilotMonitoring;
